import { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  CircularProgress,
  Toolbar,
  Typography,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AccountMenu from "./AccountMenu";
import ActivityView from "./ActivityView";
import ArchivedAndExportControls from "./ArchivedAndExportControls";
import BoardView from "./BoardView";
import DashboardView from "./DashboardView/DashboardView";
import ListView from "./ListView";
import TimelineView from "./TimelineView";
import ConfigureView from "./ConfigureView/ConfigureView";
import ViewTabs from "./ViewTabs";
import InitiativeFormDialog from "./InitiativeFormDialog/InitiativeFormDialog";
import FilterButton from "./FilterButton";
import FilterPanel from "./FilterPanel";
import SearchField from "./SearchField";
import StatusAlerts from "./StatusAlerts";
import WarningPopup from "./WarningPopup";
import { useActivity } from "../hooks/useActivity";
import { useActivityFiltering } from "../hooks/useActivityFiltering";
import { useInitiatives } from "../hooks/useInitiatives";
import { useInitiativeFiltering } from "../hooks/useInitiativeFiltering";
import { usePrivileges } from "../hooks/usePrivileges";
import { useSessionState } from "../hooks/useSessionState";
import { useCloseOnNavigate } from "../hooks/useCloseOnNavigate";
import { useLookupColorMaps } from "../hooks/useLookups";
import { ACTIVITY_FILTER_CATEGORIES } from "../constants/filterCategories";

export default function AppContent({ signOut, user }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { can, role } = usePrivileges();
  const [presentationMode, setPresentationMode] = useSessionState(
    "presentationMode",
    false,
  );
  const checkPermission = presentationMode ? () => false : can;

  const isActivityRoute = location.pathname === "/activity";
  const showInitiativeFilterChrome =
    !isActivityRoute && location.pathname !== "/configure";
  const showFilterChrome = isActivityRoute || showInitiativeFilterChrome;

  const [showArchived, setShowArchived] = useState(false);
  const {
    initiatives,
    loading,
    error,
    writeError,
    clearWriteError,
    createInitiative,
    importInitiatives,
    updateInitiative,
    archiveInitiative,
    bulkArchiveInitiatives,
    unarchiveInitiative,
    deleteInitiative,
    addComment,
    editComment,
    deleteComment,
  } = useInitiatives(showArchived);
  const { error: lookupColorsError } = useLookupColorMaps();
  const { activity, states: activityStates, refresh } = useActivity();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    filters,
    searchQuery,
    setSearchQuery,
    visibleInitiatives,
    dashboardInitiatives,
    handleToggleFilter,
    handleClearFilters,
    handleMetricClick,
  } = useInitiativeFiltering(initiatives);

  const {
    activityFilters,
    activityDateRange,
    setActivityDateRange,
    activityFiltersForButton,
    activityMatchItem,
    activityVisibleEntries,
    handleToggleActivityFilter,
    handleClearActivityFilters,
  } = useActivityFiltering(activity);

  useEffect(() => {
    if (["/dashboard", "/activity", "/configure"].includes(location.pathname))
      setSearchQuery("");
  }, [location.pathname, setSearchQuery]);

  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (isActivityRoute && prevPathRef.current !== "/activity") {
      refresh();
    }
    prevPathRef.current = location.pathname;
  }, [isActivityRoute, location.pathname, refresh]);

  useCloseOnNavigate(() => {
    setIsCreateOpen(false);
    setEditingInitiative(null);
  });

  const initiativeMutations = {
    onEditInitiative: checkPermission("initiative:edit")
      ? setEditingInitiative
      : undefined,
    onInitiativeArchive: checkPermission("initiative:archive")
      ? archiveInitiative
      : undefined,
    onInitiativeUnarchive: checkPermission("initiative:archive")
      ? unarchiveInitiative
      : undefined,
    onInitiativeDelete: checkPermission("initiative:delete")
      ? deleteInitiative
      : undefined,
    onInitiativeAddComment: checkPermission("initiative:edit")
      ? addComment
      : undefined,
    onInitiativeEditComment: checkPermission("initiative:edit")
      ? editComment
      : undefined,
    onInitiativeDeleteComment: checkPermission("initiative:edit")
      ? deleteComment
      : undefined,
    currentUserId: user?.localAccountId,
    isAdmin: role === "admin",
  };

  const activityFilterButtonProps = {
    filters: activityFiltersForButton,
    onClearAll: handleClearActivityFilters,
  };

  const initiativeFilterButtonProps = {
    filters,
    onClearAll: handleClearFilters,
  };

  const activityFilterPanelProps = {
    items: activity,
    filters: activityFilters,
    searchQuery: "",
    onToggle: handleToggleActivityFilter,
    categories: ACTIVITY_FILTER_CATEGORIES,
    matchItem: activityMatchItem,
    itemLabel: { singular: "entry", plural: "entries" },
    dateRange: activityDateRange,
    onDateRangeChange: setActivityDateRange,
  };

  const initiativeFilterPanelProps = {
    items: initiatives,
    filters,
    searchQuery,
    onToggle: handleToggleFilter,
  };

  const styles = {
    root: { minHeight: "100vh", bgcolor: "background.page" },
    appBar: { bgcolor: "background.appBar" },
    title: { fontWeight: 700, flexGrow: 1 },
    headerRow: {
      alignItems: "flex-start",
      bgcolor: "background.tableHeader",
      borderBottom: 1,
      borderColor: "divider",
      px: 2,
      display: "flex",
      justifyContent: "space-between",
    },
    stickyHeader: { position: "sticky", top: 0, zIndex: theme.zIndex.appBar },
    loadingContainer: { display: "flex", justifyContent: "center", py: 8 },
    empty: { p: 3, display: "flex", justifyContent: "center" },
    emptyText: { color: "background.textMuted" },
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.stickyHeader}>
        <AppBar position="static" elevation={1}>
          <Toolbar sx={styles.appBar}>
            <Typography variant="h6" component="h1" sx={styles.title}>
              VeroSource Roadmap
            </Typography>
            <AccountMenu
              user={user}
              signOut={() => {
                setPresentationMode(false);
                signOut();
              }}
              presentationMode={presentationMode}
              onTogglePresentationMode={() => setPresentationMode((v) => !v)}
            />
          </Toolbar>
        </AppBar>
        <ViewTabs
          setIsOpen={() => setIsCreateOpen(true)}
          can={checkPermission}
        />
      </Box>
      {showFilterChrome && (
        <Stack direction="row" sx={styles.headerRow}>
          <FilterButton
            isOpen={isFilterOpen}
            onToggleOpen={() => setIsFilterOpen((v) => !v)}
            {...(isActivityRoute
              ? activityFilterButtonProps
              : initiativeFilterButtonProps)}
          />
          {showInitiativeFilterChrome && location.pathname !== "/dashboard" && (
            <SearchField
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search initiatives..."
            />
          )}
          {showInitiativeFilterChrome && (
            <ArchivedAndExportControls
              can={checkPermission}
              showArchived={showArchived}
              onToggleShowArchived={setShowArchived}
              initiatives={visibleInitiatives}
              onImport={importInitiatives}
            />
          )}
        </Stack>
      )}
      {showFilterChrome && (
        <FilterPanel
          isOpen={isFilterOpen}
          {...(isActivityRoute
            ? activityFilterPanelProps
            : initiativeFilterPanelProps)}
        />
      )}
      <InitiativeFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={createInitiative}
      />
      <InitiativeFormDialog
        open={Boolean(editingInitiative)}
        initiative={editingInitiative}
        onClose={() => setEditingInitiative(null)}
        onSubmit={(data) => updateInitiative(editingInitiative.id, data)}
      />
      <StatusAlerts
        alerts={[
          { message: error, severity: "error" },
          {
            message:
              lookupColorsError &&
              "Couldn't load lookup colors — colors may not display correctly.",
            severity: "warning",
          },
        ]}
      />
      <WarningPopup message={writeError} onClose={clearWriteError} />
      {loading ? (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : visibleInitiatives.length === 0 && showInitiativeFilterChrome ? (
        <Box sx={styles.empty}>
          <Typography sx={styles.emptyText}>
            No initiatives exist. Use 'New Initiative' above to create one.
          </Typography>
        </Box>
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <DashboardView
                initiatives={dashboardInitiatives}
                user={user}
                presentationMode={presentationMode}
                onMetricClick={(key, value) => {
                  handleMetricClick(key, value);
                  navigate("/list");
                }}
              />
            }
          />
          <Route
            path="/list"
            element={
              <ListView
                initiatives={visibleInitiatives}
                mutations={initiativeMutations}
                onBulkArchive={
                  checkPermission("initiative:archive")
                    ? bulkArchiveInitiatives
                    : undefined
                }
              />
            }
          />
          <Route
            path="/board"
            element={
              <BoardView
                initiatives={visibleInitiatives}
                mutations={initiativeMutations}
                onInitiativeChange={
                  checkPermission("initiative:edit")
                    ? updateInitiative
                    : undefined
                }
              />
            }
          />
          <Route
            path="/timeline"
            element={
              <TimelineView
                initiatives={visibleInitiatives}
                mutations={initiativeMutations}
                onInitiativeChange={
                  checkPermission("initiative:edit")
                    ? updateInitiative
                    : undefined
                }
              />
            }
          />
          <Route
            path="/activity"
            element={
              checkPermission("initiative:view_activity") ? (
                <ActivityView
                  activity={activity}
                  visibleActivity={activityVisibleEntries}
                  states={activityStates}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/configure"
            element={
              checkPermission("lookup:create") ||
              checkPermission("lookup:edit") ||
              checkPermission("lookup:delete") ? (
                <ConfigureView can={checkPermission} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
        </Routes>
      )}
    </Box>
  );
}
